/**
 * EditAsiMilestoneLinkModal — manual ASI <-> milestone link repair.
 *
 * ASIs are architect-driven and modeled as milestones (never threads — see the Construction-
 * support dividing line in CLAUDE.md). Most ASIs get their milestone created automatically
 * (AddAsiModal.jsx's createAsiMilestone), but a handful of legacy ASIs from before that flow
 * existed are still linked to a Cortex Thread instead. Rather than an automated backfill (which
 * would create real milestones — checklist, date-driven urgency scores — for historical,
 * already-resolved work), this gives the user a manual fix: search the project's ASI-type
 * milestones and pick the right one, or clear a bad link.
 *
 * Opens via CustomEvent('open-edit-asi-milestone-modal', { detail: {
 *   asiId, projectId, contractId, currentMilestoneId, asiNumber, subject
 * } }).
 * Busts ctx.asiCache[contractId] + dispatches outline-render on success.
 */

import { useState, useEffect, useMemo } from 'react'
import { ctx } from '../outline/ctx.js'
import { wf } from '../outline/state.js'
import { getProjectAsiMilestones } from '../services/milestones.js'
import { linkAsiMilestone } from '../services/asis.js'
import { getFirst } from '../outline/utils.js'

const labelStyle = { display: 'block', fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-mid)', letterSpacing: '0.04em', marginBottom: 4, textTransform: 'uppercase' }
const inputStyle = { width: '100%', padding: '7px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, boxSizing: 'border-box', outline: 'none' }

function fmtDate(v) {
  if (!v) return ''
  const [y, m, d] = String(v).slice(0, 10).split('-')
  return m && d && y ? `${m}/${d}/${y}` : ''
}

export default function EditAsiMilestoneLinkModal() {
  const [open, setOpen] = useState(false)
  const [asiId, setAsiId] = useState(null)
  const [contractId, setContractId] = useState(null)
  const [currentMilestoneId, setCurrentMilestoneId] = useState(null)
  const [label, setLabel] = useState('')
  const [milestones, setMilestones] = useState(null) // null = not loaded
  const [selectedId, setSelectedId] = useState(null)
  const [search, setSearch] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (open) { document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = '' } }
  }, [open])

  useEffect(() => {
    function onOpen(e) {
      const d = e.detail || {}
      setAsiId(d.asiId || null)
      setContractId(d.contractId || null)
      setCurrentMilestoneId(d.currentMilestoneId || null)
      setSelectedId(d.currentMilestoneId || null)
      setLabel(['ASI', d.asiNumber].filter(Boolean).join(' ') + (d.subject ? ' — ' + d.subject : ''))
      setSearch(''); setError(null); setSubmitting(false)
      setMilestones(null)
      setOpen(true)
      if (d.projectId) {
        getProjectAsiMilestones(d.projectId).then(setMilestones).catch(() => setMilestones([]))
      } else {
        setMilestones([])
      }
    }
    window.addEventListener('open-edit-asi-milestone-modal', onOpen)
    return () => window.removeEventListener('open-edit-asi-milestone-modal', onOpen)
  }, [])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && open) setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  const filtered = useMemo(() => {
    if (!Array.isArray(milestones)) return []
    const q = search.trim().toLowerCase()
    if (!q) return milestones
    return milestones.filter(m => (m.fields['Subtitle'] || getFirst(m.fields['Full Deliverable Name']) || '').toLowerCase().includes(q))
  }, [milestones, search])

  async function save(nextId) {
    setSubmitting(true); setError(null)
    try {
      await linkAsiMilestone(asiId, nextId)
      if (ctx.asiCache && contractId) ctx.asiCache[contractId] = undefined
      // Reverse-mount caches (ASI shown under its milestone) key by milestone id — bust both the
      // old and new so neither side shows a stale mount after this repair.
      if (ctx.milestoneAsiCache) {
        if (currentMilestoneId) ctx.milestoneAsiCache[currentMilestoneId] = undefined
        if (nextId) ctx.milestoneAsiCache[nextId] = undefined
      }
      setOpen(false)
      window.dispatchEvent(new Event('outline-render'))
    } catch (ex) {
      setError(ex.message || 'Failed to update link'); setSubmitting(false)
    }
  }

  if (!open) return null

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div data-modal-id="EditAsiMilestoneLinkModal" style={{ background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 8, boxShadow: '0 12px 36px rgba(0,0,0,0.16)', width: 480, padding: '20px 20px 16px', maxHeight: 'calc(100vh - 40px)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, flexShrink: 0 }}>
          <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' }}>Edit Milestone Link</span>
          <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 16, lineHeight: 1, padding: 2 }}>×</button>
        </div>
        <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-mid)', marginBottom: 14, flexShrink: 0 }}>{label}</div>

        <div style={{ flexShrink: 0, marginBottom: 10 }}>
          <label style={labelStyle}>Search milestones</label>
          <input style={inputStyle} value={search} onChange={e => setSearch(e.target.value)} placeholder="Filter by subtitle…" autoFocus />
        </div>

        <div style={{ flex: 1, overflowY: 'auto', border: '0.5px solid var(--aj-border)', borderRadius: 4, minHeight: 160, maxHeight: 320 }}>
          {milestones === null && (
            <div style={{ padding: 14, fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-mid)' }}>Loading milestones…</div>
          )}
          {milestones !== null && filtered.length === 0 && (
            <div style={{ padding: 14, fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-mid)' }}>
              No "Architects Supplemental Instructions" milestones found in this project.
            </div>
          )}
          {filtered.map(m => {
            const isSel = selectedId === m.id
            const subtitle = m.fields['Subtitle'] || getFirst(m.fields['Full Deliverable Name']) || 'Untitled'
            const posted = !!m.fields['Posted Date']
            return (
              <div key={m.id} onClick={() => setSelectedId(m.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', cursor: 'pointer',
                  background: isSel ? 'var(--aj-bg-subtle)' : 'transparent',
                  borderBottom: '0.5px solid var(--aj-border)',
                }}>
                <div style={{ width: 14, height: 14, borderRadius: '50%', border: `1.5px solid ${isSel ? 'var(--aj-text)' : 'var(--aj-border-mid)'}`, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {isSel && <div style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--aj-text)' }} />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: 'var(--aj-text)', fontWeight: m.id === currentMilestoneId ? 600 : 400 }}>{subtitle}</div>
                  {m.id === currentMilestoneId && <div style={{ fontFamily: '"DM Mono",monospace', fontSize: 10, color: '#1D9E75' }}>Currently linked</div>}
                </div>
                {m.fields['Latest Finish (LFT)'] && (
                  <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 10.5, color: 'var(--aj-text-mid)', flexShrink: 0 }}>{fmtDate(m.fields['Latest Finish (LFT)'])}</span>
                )}
                <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9.5, fontWeight: 600, letterSpacing: '0.04em', color: posted ? '#1D9E75' : 'var(--aj-text-muted)', flexShrink: 0 }}>{posted ? 'POSTED' : 'OPEN'}</span>
              </div>
            )
          })}
        </div>

        {error && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#c0392b', marginTop: 10, flexShrink: 0 }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 14, flexShrink: 0 }}>
          <button onClick={() => save(null)} disabled={submitting || !currentMilestoneId}
            style={{ padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'none', border: '0.5px solid rgba(192,57,43,0.4)', borderRadius: 4, cursor: (submitting || !currentMilestoneId) ? 'default' : 'pointer', color: '#c0392b', opacity: (submitting || !currentMilestoneId) ? 0.5 : 1 }}>
            Clear link
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setOpen(false)} disabled={submitting} style={{ padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)' }}>Cancel</button>
            <button onClick={() => save(selectedId)} disabled={submitting || !selectedId || selectedId === currentMilestoneId}
              style={{ padding: '6px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: submitting ? 'var(--aj-bg-muted)' : '#07272D', color: submitting ? 'var(--aj-text-muted)' : '#fff', border: 'none', borderRadius: 4, cursor: (submitting || !selectedId || selectedId === currentMilestoneId) ? 'default' : 'pointer', opacity: (!selectedId || selectedId === currentMilestoneId) ? 0.5 : 1 }}>
              {submitting ? 'Saving…' : 'Save link'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
